import Button from '@components/ui/Button'
import { Typewriter } from '@components/ui/text-animate'
import { AnimatedGradientText } from '@components/ui/text-animate-enhanced'
import { useEffect, useId, useState } from 'react'
import { useNavigate } from 'react-router-dom'

const HomePage = () => {
  const navigate = useNavigate()
  const featuresId = useId()
  const [_hoveredFeature, setHoveredFeature] = useState<number | null>(null)

  // 초기 렌더링 시 localStorage 직접 확인
  const recentEventsStr = localStorage.getItem('recentEvents')
  const hasRecentEvents = recentEventsStr !== null && JSON.parse(recentEventsStr).length > 0

  useEffect(() => {
    // 브라우저의 스크롤 복원 기능 비활성화
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'
    }

    // 페이지 로드 시 최상단으로 스크롤
    window.scrollTo(0, 0)

    // 컴포넌트 언마운트 시 스크롤 복원 기능 다시 활성화
    return () => {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto'
      }
    }
  }, [])

  const features = [
    {
      icon: '⏰',
      title: '카운트다운',
      description: '설레는 순간을 실시간 카운트다운과 함께 공유하세요',
      gradient: 'from-purple-400 to-purple-600',
      color: 'from-purple-400 to-purple-600',
      iconBg: 'bg-gradient-to-br from-purple-100 to-purple-200',
    },
    {
      icon: '🎫',
      title: '스크래치 카드',
      description: '복권처럼 긁어서 아기의 성별을 확인하는 재미있는 방식',
      gradient: 'from-pink-400 to-pink-600',
      color: 'from-pink-400 to-pink-600',
      iconBg: 'bg-gradient-to-br from-pink-100 to-pink-200',
    },
    {
      icon: '🎰',
      title: '행운의 룰렛',
      description: '돌려서 결과를 확인하는 스릴 넘치는 공개 방식',
      gradient: 'from-blue-400 to-blue-600',
      color: 'from-blue-400 to-blue-600',
      iconBg: 'bg-gradient-to-br from-blue-100 to-blue-200',
    },
  ]

  const steps = [
    {
      number: '1',
      title: '템플릿 선택',
      description: '마음에 드는 공개 방식을 선택하세요',
      icon: '🎨',
      color: 'from-purple-400 to-purple-600',
    },
    {
      number: '2',
      title: '정보 입력',
      description: '이벤트 정보와 공개 일시를 설정하세요',
      icon: '✏️',
      color: 'from-pink-400 to-pink-600',
    },
    {
      number: '3',
      title: '링크 공유',
      description: '가족과 친구들에게 초대 링크를 보내세요',
      icon: '📤',
      color: 'from-blue-400 to-blue-600',
    },
  ]

  return (
    <div className="min-h-screen bg-cream-200">
      {/* Navigation Bar */}
      <nav className="sticky top-0 z-50 glass border-b border-white/20" aria-label="주 네비게이션">
        <div className="container-main py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-warm rounded-xl flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <title>선물 아이콘</title>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
                  />
                </svg>
              </div>
              <span className="text-xl font-bold text-gradient-warm">젠더리빌</span>
            </div>
            <div className="flex items-center space-x-4">
              {hasRecentEvents && (
                <button
                  type="button"
                  onClick={() => navigate('/host')}
                  className="text-sm font-medium text-neutral-700 hover:text-neutral-900 transition-colors"
                >
                  내 이벤트
                </button>
              )}
              <Button variant="primary" size="sm" onClick={() => navigate('/create')} gradient>
                시작하기
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-float"></div>
          <div
            className="absolute -bottom-40 -left-40 w-80 h-80 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-float"
            style={{ animationDelay: '2s' }}
          ></div>
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-yellow-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float"
            style={{ animationDelay: '4s' }}
          ></div>
        </div>

        <div className="relative container-main py-12 sm:py-16 lg:py-32">
          <div className="text-center space-y-6 sm:space-y-8 max-w-3xl mx-auto animate-slide-up">
            {/* Badge */}
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-sm font-medium">
              <span className="mr-1">✨</span>
              <Typewriter text="3분 만에 완성하는 특별한 순간" speed={30} delay={0.5} cursor={false} />
            </div>

            {/* Main heading */}
            <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold leading-tight space-y-1 sm:space-y-2">
              <span className="block text-neutral-900">소중한 아기의</span>
              <AnimatedGradientText
                className="block text-4xl sm:text-6xl lg:text-8xl font-bold"
                colors={['#FF6B6B', '#4ECDC4', '#FFE66D']}
                animationSpeed={5}
                delay={0.3}
              >
                특별한 순간
              </AnimatedGradientText>
            </h1>
            <div className="text-base sm:text-xl lg:text-2xl text-neutral-600 max-w-3xl mx-auto leading-relaxed px-4 sm:px-0">
              <p>가족과 친구들과 함께 아기의 성별을 공개하는</p>
              <p>가장 특별하고 감동적인 순간을 만들어보세요</p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center pt-4 sm:pt-6 px-4 sm:px-0">
              <button
                type="button"
                onClick={() => navigate('/create')}
                className="group relative w-full sm:w-auto sm:min-w-[180px] h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg font-semibold text-white overflow-hidden rounded-full shadow-xl transform hover:-translate-y-1 transition-all duration-300"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 bg-size-200 animate-gradient"></div>
                <span className="relative flex items-center justify-center gap-2">
                  지금 시작하기
                  <svg
                    className="w-5 h-5 group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <title>화살표 아이콘</title>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </button>

              {hasRecentEvents && (
                <button
                  type="button"
                  onClick={() => navigate('/host')}
                  className="w-full sm:w-auto sm:min-w-[180px] h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg font-semibold text-gray-700 bg-white/80 backdrop-blur-sm border-2 border-gray-200 rounded-full hover:border-purple-300 hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
                >
                  내 이벤트 관리
                </button>
              )}
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-xs sm:text-sm text-gray-500 pt-6 sm:pt-8">
              <div className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>무료 이용</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>앱 설치 불필요</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>안전한 데이터 보호</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id={featuresId} className="py-12 sm:py-16 lg:py-20 relative">
        <div className="container-main">
          <div className="text-center mb-8 sm:mb-12 px-4 sm:px-0">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">다양한 공개 방식</h2>
            <p className="text-base sm:text-lg text-gray-600">세 가지 재미있는 템플릿으로 특별한 순간을 만들어보세요</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 py-4 sm:py-6 px-4 sm:px-0">
            {features.map((feature, index) => (
              <button
                key={index}
                type="button"
                className="group relative flex w-full text-left h-full"
                onMouseEnter={() => setHoveredFeature(index)}
                onMouseLeave={() => setHoveredFeature(null)}
              >
                <div
                  className={`absolute inset-0 bg-gradient-to-r ${feature.color} rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300`}
                ></div>
                <div className="relative flex flex-col w-full bg-white rounded-2xl p-6 sm:p-8 shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                  <div
                    className={`inline-flex items-center justify-center w-20 h-20 ${feature.iconBg} rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300`}
                  >
                    <span className="text-4xl">{feature.icon}</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed flex-grow">{feature.description}</p>

                  {/* Hover effect indicator */}
                  <div
                    className={`absolute bottom-4 right-4 w-2 h-2 rounded-full bg-gradient-to-r ${feature.color} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
                  ></div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* How it works Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container-main">
          <div className="text-center mb-8 sm:mb-12 px-4 sm:px-0">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">간단한 3단계</h2>
            <p className="text-base sm:text-lg text-gray-600">복잡한 과정 없이 3분 만에 이벤트를 만들 수 있어요</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Connection line */}
              <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-200 via-blue-200 to-green-200 -translate-y-1/2"></div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 relative py-4 sm:py-6 px-4 sm:px-0">
                {steps.map((step, index) => (
                  <div key={index} className="relative">
                    <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                      {/* Step number */}
                      <div
                        className={`absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-r ${step.color} rounded-full flex items-center justify-center text-white font-bold shadow-lg`}
                      >
                        {step.number}
                      </div>

                      {/* Icon */}
                      <div className="text-4xl mb-4">{step.icon}</div>

                      {/* Content */}
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-to-r from-purple-100 via-pink-100 to-blue-100 opacity-50"></div>

        <div className="relative container-main text-center">
          <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6 pb-8 sm:pb-12 px-4 sm:px-0">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">준비되셨나요?</h2>
            <p className="text-base sm:text-lg text-gray-600">지금 바로 시작해서 특별한 순간을 만들어보세요</p>
            <button
              type="button"
              onClick={() => navigate('/create')}
              className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-full shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
            >
              무료로 시작하기
              <span className="text-2xl">🎉</span>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-100">
        <div className="container-main py-8 sm:py-12 px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            {/* Brand */}
            <div className="col-span-1 sm:col-span-2 md:col-span-2">
              <div className="flex items-center space-x-2 mb-3 sm:mb-4">
                <span className="text-xl sm:text-2xl">👶</span>
                <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  젠더리빌
                </span>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                특별한 순간을 더욱 특별하게 만들어드립니다.
                <br />
                가족과 친구들과 함께 기쁨을 나누세요.
              </p>
            </div>

            {/* Links */}
            <div className="col-span-1">
              <h4 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base">서비스</h4>
              <ul className="space-y-2 text-xs sm:text-sm text-gray-600">
                <li>
                  <button type="button" className="hover:text-purple-600 transition-colors">
                    이용 방법
                  </button>
                </li>
                <li>
                  <button type="button" className="hover:text-purple-600 transition-colors">
                    템플릿 소개
                  </button>
                </li>
                <li>
                  <button type="button" className="hover:text-purple-600 transition-colors">
                    자주 묻는 질문
                  </button>
                </li>
              </ul>
            </div>

            <div className="col-span-1">
              <h4 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base">문의</h4>
              <ul className="space-y-2 text-xs sm:text-sm text-gray-600">
                <li>support@genderreveal.kr</li>
                <li>카카오톡: @젠더리빌</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-100 mt-6 sm:mt-8 pt-6 sm:pt-8 text-center text-xs sm:text-sm text-gray-500">
            <p>© 2025 젠더리빌. 모든 권리 보유.</p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        .bg-size-200 {
          background-size: 200% 200%;
        }
        
        .animate-gradient {
          animation: gradient 3s ease infinite;
        }
      `}</style>
    </div>
  )
}

export default HomePage
